'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface TimelineOption {
  id: string;
  label: string;
  value: string;
}

interface TimelineResult {
  phase: string;
  duration: string;
  description: string;
}

const TimelineCalculator = () => {
  const [fleetSize, setFleetSize] = useState<string>('');
  const [urgency, setUrgency] = useState<string>('');
  const [location, setLocation] = useState<string>('');
  const [showResults, setShowResults] = useState<boolean>(false);

  const fleetSizeOptions: TimelineOption[] = [
    { id: '1-5', label: '1-5 vehicles', value: '1-5' },
    { id: '6-15', label: '6-15 vehicles', value: '6-15' },
    { id: '16-30', label: '16-30 vehicles', value: '16-30' },
    { id: '31+', label: '31+ vehicles', value: '31+' }
  ];

  const urgencyOptions: TimelineOption[] = [
    { id: 'immediate', label: 'Immediate (within 1 week)', value: 'immediate' },
    { id: 'standard', label: 'Standard (2-4 weeks)', value: 'standard' },
    { id: 'flexible', label: 'Flexible (1-2 months)', value: 'flexible' }
  ];

  const locationOptions: TimelineOption[] = [
    { id: 'single', label: 'Single location', value: 'single' },
    { id: 'regional', label: 'Regional (same state)', value: 'regional' },
    { id: 'multi-state', label: 'Multi-state', value: 'multi-state' }
  ];

  const calculateTimeline = (): TimelineResult[] => {
    const baseTimeline: TimelineResult[] = [
      {
        phase: 'Initial Contact & Assessment',
        duration: '1-2 days',
        description: 'Information gathering and preliminary evaluation'
      },
      {
        phase: 'On-Site Inspection',
        duration: '2-5 days',
        description: 'Comprehensive vehicle inspection and documentation'
      },
      {
        phase: 'Market Analysis & Offer',
        duration: '1-3 days',
        description: 'Valuation analysis and offer preparation'
      },
      {
        phase: 'Negotiation & Agreement',
        duration: '2-4 days',
        description: 'Terms finalization and contract execution'
      },
      {
        phase: 'Transaction Completion',
        duration: '3-7 days',
        description: 'Payment processing and vehicle pickup'
      }
    ];

    // Adjust timeline based on selections
    if (fleetSize === '31+') {
      baseTimeline[1].duration = '5-10 days';
      baseTimeline[4].duration = '7-14 days';
    }

    if (urgency === 'immediate') {
      baseTimeline[0].duration = 'Same day';
      baseTimeline[2].duration = '1-2 days';
      baseTimeline[3].duration = '1-2 days';
    }

    if (location === 'multi-state') {
      baseTimeline[1].duration = '5-10 days';
      baseTimeline[4].duration = '7-14 days';
    }

    return baseTimeline;
  };

  const handleCalculate = () => {
    if (fleetSize && urgency && location) {
      setShowResults(true);
    }
  };

  const handleReset = () => {
    setFleetSize('');
    setUrgency('');
    setLocation('');
    setShowResults(false);
  };

  const getTotalEstimate = (): string => {
    if (urgency === 'immediate' && fleetSize === '1-5') return '7-14 days';
    if (urgency === 'immediate') return '14-21 days';
    if (fleetSize === '31+' || location === 'multi-state') return '21-35 days';
    return '14-28 days';
  };

  return (
    <section className="py-16 md:py-24 bg-card">
      <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 mb-4">
            <Icon name="CalculatorIcon" size={28} variant="solid" className="text-primary" />
            <span className="text-sm font-semibold font-headline text-primary uppercase tracking-wider">
              Timeline Estimator
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold font-headline text-foreground mb-4">
            Estimate Your Transaction Timeline
          </h2>
          <p className="text-lg text-muted-foreground">
            Get a customized timeline estimate based on your specific fleet characteristics and requirements.
          </p>
        </div>

        <div className="bg-background rounded-lg p-8 border border-border shadow-brand">
          {!showResults ? (
            <div className="space-y-6">
              {/* Fleet Size Selection */}
              <div>
                <label className="block text-sm font-semibold text-foreground mb-3">
                  Fleet Size
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {fleetSizeOptions.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => setFleetSize(option.value)}
                      className={`p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                        fleetSize === option.value
                          ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-foreground">{option.label}</span>
                        {fleetSize === option.value && (
                          <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-primary" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Urgency Selection */}
              <div>
                <label className="block text-sm font-semibold text-foreground mb-3">
                  Timeline Urgency
                </label>
                <div className="grid grid-cols-1 gap-3">
                  {urgencyOptions.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => setUrgency(option.value)}
                      className={`p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                        urgency === option.value
                          ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-foreground">{option.label}</span>
                        {urgency === option.value && (
                          <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-primary" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Location Selection */}
              <div>
                <label className="block text-sm font-semibold text-foreground mb-3">
                  Fleet Location
                </label>
                <div className="grid grid-cols-1 gap-3">
                  {locationOptions.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => setLocation(option.value)}
                      className={`p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                        location === option.value
                          ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-foreground">{option.label}</span>
                        {location === option.value && (
                          <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-primary" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Calculate Button */}
              <button
                onClick={handleCalculate}
                disabled={!fleetSize || !urgency || !location}
                className={`w-full py-4 rounded-lg font-semibold transition-all duration-200 ${
                  fleetSize && urgency && location
                    ? 'bg-cta text-cta-foreground hover:bg-opacity-90 hover:-translate-y-0.5 shadow-brand'
                    : 'bg-muted text-muted-foreground cursor-not-allowed'
                }`}
              >
                Calculate Timeline
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Results Header */}
              <div className="text-center pb-6 border-b border-border">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-success/10 rounded-full mb-4">
                  <Icon name="ClockIcon" size={32} variant="solid" className="text-success" />
                </div>
                <h3 className="text-2xl font-bold font-headline text-foreground mb-2">
                  Estimated Total Timeline
                </h3>
                <p className="text-4xl font-bold text-primary">{getTotalEstimate()}</p>
              </div>

              {/* Timeline Breakdown */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-foreground mb-4">
                  Phase-by-Phase Breakdown
                </h4>
                {calculateTimeline().map((phase, index) => (
                  <div
                    key={index}
                    className="flex items-start space-x-4 p-4 bg-card rounded-lg border border-border"
                  >
                    <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                      <span className="text-sm font-bold text-primary">{index + 1}</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h5 className="font-semibold text-foreground">{phase.phase}</h5>
                        <span className="text-sm font-medium text-primary">{phase.duration}</span>
                      </div>
                      <p className="text-sm text-muted-foreground">{phase.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Disclaimer */}
              <div className="bg-warning/10 border border-warning/30 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Icon name="InformationCircleIcon" size={20} variant="solid" className="text-warning flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-foreground">
                    <strong>Note:</strong> These are estimated timelines based on typical scenarios. Actual timelines may vary depending on vehicle condition, documentation availability, and other factors. Contact us for a more precise timeline for your specific situation.
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <button
                  onClick={handleReset}
                  className="flex-1 py-3 px-6 bg-muted text-foreground font-semibold rounded-lg hover:bg-opacity-80 transition-all duration-200"
                >
                  Calculate Again
                </button>
                <a
                  href="/contact"
                  className="flex-1 py-3 px-6 bg-cta text-cta-foreground font-semibold rounded-lg hover:bg-opacity-90 transition-all duration-200 text-center"
                >
                  Get Started Now
                </a>
              </div>
            </div>
          )}
        </div>

        {/* Additional Info */}
        <div className="mt-8 text-center">
          <p className="text-sm text-muted-foreground">
            Need a faster timeline? We offer expedited services for urgent fleet liquidation needs.{' '}
            <a href="/contact" className="text-primary font-semibold hover:underline">
              Contact us to discuss options
            </a>
          </p>
        </div>
      </div>
    </section>
  );
};

export default TimelineCalculator;